import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useWebSocket } from './hooks/useWebSocket'
import { useShards } from './hooks/useShards'
import { useWorkers } from './hooks/useWorkers'
import { useTheme } from './contexts/ThemeContext'
import { Header } from './components/Header'
import { Loader } from './components/Loader'
import { DisconnectWarning } from './components/DisconnectWarning'
import { LogsPanel } from './components/LogsPanel'
import { BoardView } from './components/BoardView'
import { MissionControl } from './components/MissionControl'
import { GraphView, type Worker as GraphWorker, type Shard as GraphShard } from './components/GraphView'
import { Settings } from './components/Settings'
import { ShardInspectorModal } from './components/ShardInspectorModal'
import { WorkerModal } from './components/WorkerModal'
import { BlockedShardToast, type BlockedShardNotification } from './components/BlockedShardToast'
import { CompletionModal } from './components/CompletionModal'
import { InteractionPanel } from './components/InteractionPanel'
import { ProjectLauncher } from './components/ProjectLauncher'
import { playNotificationSound } from './utils/sounds'
import { Shard, LogEntry } from './types/shard'
import { Worker } from './hooks/useWorkers'
import { Question } from './types/interaction'
import { PublicClientApplication, InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser'
import { postAction, createMsalSession, idTokenExpired, type AuthSession } from './lib/authFetch'

type ViewMode = 'graph' | 'board' | 'mission-control'

// MSAL configuration - reads from Vite env vars
// Set VITE_AZURE_CLIENT_ID and VITE_AZURE_TENANT_ID to enable Entra ID auth.
// When not set, auth is skipped (dev mode).
const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || 'common'}`,
    redirectUri: window.location.origin,
  },
}

const msalEnabled = !!import.meta.env.VITE_AZURE_CLIENT_ID
let msalInstance: PublicClientApplication | null = null
if (msalEnabled) {
  msalInstance = new PublicClientApplication(msalConfig)
}

// OIDC scopes only — no api:// URI needed.
// MSAL treats OIDC-only scopes as clientId-scoped, so the ID token has aud=clientId.
const loginScopes = ['openid', 'profile', 'email']

// Derive WebSocket URL from the current page URL (works in both local and cloud)
function getWsUrl(token?: string | null): string {
  const base = import.meta.env.VITE_WS_URL
    ? import.meta.env.VITE_WS_URL
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`
  // Pass auth token as query param (WebSocket API doesn't support custom headers)
  if (token) return `${base}?token=${encodeURIComponent(token)}`
  return base
}

// Check URL for API key (?key=xxx) and persist in sessionStorage
function getApiKeyFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search)
  const key = params.get('key')
  if (key) {
    sessionStorage.setItem('starforge_api_key', key)
    // Clean the key from the URL bar (keep it out of history/bookmarks)
    const url = new URL(window.location.href)
    url.searchParams.delete('key')
    window.history.replaceState({}, '', url.toString())
    return key
  }
  return sessionStorage.getItem('starforge_api_key')
}

function App() {
  const [isInitializing, setIsInitializing] = useState(true)
  const [projectName, setProjectName] = useState('')
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [_authAccount, setAuthAccount] = useState<AccountInfo | null>(null)
  const [authLoading, setAuthLoading] = useState(msalEnabled)
  const [graphToken, setGraphToken] = useState<string | null>(null)
  const [graphTokenError, setGraphTokenError] = useState<string | null>(null)
  const { settings } = useTheme()

  // On mount, check for API key in URL or sessionStorage
  useEffect(() => {
    if (!msalEnabled) {
      const apiKey = getApiKeyFromUrl()
      if (apiKey) {
        setAuthToken(apiKey)
      }
      setAuthLoading(false)
    }
  }, [])

  const handleSignOut = useCallback(async () => {
    if (msalInstance) {
      try {
        await msalInstance.initialize()
        await msalInstance.logoutRedirect()
      } catch {
        // Clear local state as fallback
        setAuthToken(null)
        setAuthAccount(null)
        setGraphToken(null)
        window.location.reload()
      }
    }
  }, [])

  // Helper to build fetch headers with auth token
  const getAuthHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }
    return headers
  }, [authToken])

  // The token as of now, for the auth session below: a refreshed token must
  // reach the retry before React re-renders with it.
  const authTokenRef = useRef<string | null>(null)
  authTokenRef.current = authToken

  // Start, stop and resume go through authFetch, which refreshes an expired
  // token once and otherwise sends the user to sign in (L3A2-5). The session
  // reads refs only, so it is created once and never changes identity.
  const authSession = useMemo<AuthSession>(
    () => createMsalSession(msalInstance, authTokenRef, setAuthToken, loginScopes),
    [],
  )

  // A failed stop or resume, shown in a banner until dismissed.
  const [actionError, setActionError] = useState<string | null>(null)

  // Entra ID authentication
  useEffect(() => {
    if (!msalInstance) return

    async function acquireGraphToken(account: AccountInfo) {
      const graphScopes = ['https://graph.microsoft.com/GroupMember.Read.All', 'https://graph.microsoft.com/User.Read.All']
      try {
        // Try silent first — works if user previously consented to this scope
        const graphResponse = await msalInstance!.acquireTokenSilent({
          account,
          scopes: graphScopes,
        })
        setGraphToken(graphResponse.accessToken)
      } catch (err: unknown) {
        if (err instanceof InteractionRequiredAuthError) {
          // User hasn't consented to Graph scope yet — need interactive consent
          try {
            const graphResponse = await msalInstance!.acquireTokenPopup({
              account,
              scopes: graphScopes,
            })
            setGraphToken(graphResponse.accessToken)
          } catch (popupErr: unknown) {
            const msg = popupErr instanceof Error ? popupErr.message : String(popupErr)
            console.error('Graph token popup failed:', popupErr)
            setGraphTokenError(msg)
          }
        } else {
          const msg = err instanceof Error ? err.message : String(err)
          console.error('Graph token silent failed:', err)
          setGraphTokenError(msg)
        }
      }
    }

    async function initAuth() {
      try {
        await msalInstance!.initialize()

        // Handle redirect callback (returning from Microsoft login page)
        const response = await msalInstance!.handleRedirectPromise()
        if (response) {
          setAuthToken(response.idToken)
          setAuthAccount(response.account)
          setAuthLoading(false)
          if (response.account) {
            acquireGraphToken(response.account)
          } else {
            setGraphTokenError('No account in login response')
          }
          return
        }

        // Check for existing account (already logged in)
        const accounts = msalInstance!.getAllAccounts()
        if (accounts.length > 0) {
          try {
            let tokenResponse = await msalInstance!.acquireTokenSilent({
              account: accounts[0],
              scopes: loginScopes,
            })
            // MSAL's cache checks the access token's expiry, not the ID
            // token's, so a reload can get a dead ID token back and every
            // background call would 401. Force one refresh; if that fails,
            // carry on with the cached token as before (L3A2-5).
            if (idTokenExpired(tokenResponse.idTokenClaims)) {
              try {
                tokenResponse = await msalInstance!.acquireTokenSilent({
                  account: accounts[0],
                  scopes: loginScopes,
                  forceRefresh: true,
                })
              } catch (err) {
                console.warn('Refreshing the expired cached ID token failed:', err)
              }
            }
            setAuthToken(tokenResponse.idToken)
            setAuthAccount(tokenResponse.account)
            setAuthLoading(false)
            acquireGraphToken(accounts[0]!)
          } catch {
            // Silent token acquisition failed, redirect to login
            await msalInstance!.acquireTokenRedirect({ scopes: loginScopes })
          }
        } else {
          // No account — redirect to login
          await msalInstance!.acquireTokenRedirect({ scopes: loginScopes })
        }
      } catch (err) {
        console.error('MSAL initialization failed:', err)
        setAuthLoading(false)
      }
    }

    initAuth()
  }, [])

  // Update page title with project name
  useEffect(() => {
    document.title = projectName ? `Star Forge | ${projectName}` : 'Star Forge'
  }, [projectName])
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [selectedShard, setSelectedShard] = useState<Shard | null>(null)
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('graph')
  const [isLogsOpen, setIsLogsOpen] = useState(false)
  const [blockedNotifications, setBlockedNotifications] = useState<BlockedShardNotification[]>([])
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [questions, setQuestions] = useState<Question[]>([])
  const [showProjectLauncher, setShowProjectLauncher] = useState(false)
  const [activeWorkflowId, setActiveWorkflowId] = useState<string | null>(null)
  // The true project_name of the project on screen (see the lookups below),
  // which is what a question's project_name carries.
  const [viewedWorkflowName, setViewedWorkflowName] = useState('')
  const activeLookupRef = useRef(0)

  // Find the running workflow of the project now on screen. The Stop button
  // sends whatever id this leaves, so the old id is cleared at once and only
  // the latest lookup may set a new one: a slow answer for the project viewed
  // before must not land after the switch (L3-10).
  const discoverActiveWorkflow = useCallback((lookupName: string) => {
    const lookup = ++activeLookupRef.current
    setActiveWorkflowId(null)
    setViewedWorkflowName(lookupName)
    fetch(`/api/projects/active/${encodeURIComponent(lookupName)}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => { if (activeLookupRef.current === lookup) setActiveWorkflowId(d.workflow_id || null) })
      .catch(() => { if (activeLookupRef.current === lookup) setActiveWorkflowId(null) })
  }, [getAuthHeaders])
  const [projectListKey, setProjectListKey] = useState(0)
  const [projectResidency, setProjectResidency] = useState<'au' | 'none'>('none')

  // Read residency from the project's .starforge/spark.yaml whenever the
  // selected project changes. The /api/projects/file endpoint reads from the
  // currently-selected project's repo (cloned to disk by repo_sync_loop), so
  // this is the authoritative source. Simple regex parse avoids pulling in a
  // YAML dependency for one boolean-ish flag.
  useEffect(() => {
    if (!projectName) {
      setProjectResidency('none')
      return
    }
    let cancelled = false
    fetch('/api/projects/file?path=.starforge/spark.yaml', { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.text() : null))
      .then(text => {
        if (cancelled) return
        if (text && /^residency:\s*au\s*$/m.test(text)) {
          setProjectResidency('au')
        } else {
          setProjectResidency('none')
        }
      })
      .catch(() => {
        if (!cancelled) setProjectResidency('none')
      })
    return () => {
      cancelled = true
    }
  }, [projectName, getAuthHeaders])

  // Handle starting a project via the Temporal dispatcher
  const [isLaunching, setIsLaunching] = useState(false)
  const handleLaunchProject = useCallback(async (request: { projectName: string; repoUrl: string; branch: string; intent: string; schedule?: { activeHoursStart: number; activeHoursEnd: number; timezone: string }; members?: string[]; residency?: 'au' | 'none' }) => {
    setIsLaunching(true)
    try {
      const body: Record<string, any> = { project_name: request.projectName, repo_url: request.repoUrl, intent: request.intent }
      if (request.members && request.members.length > 0) {
        body.members = request.members
      }
      // Omit residency when 'none' so the Rust backend's #[serde(default)]
      // path stays the production code path (locked in by api.rs deserialize
      // test from Task 7). Matches the conditional-add pattern used for members.
      if (request.residency && request.residency !== 'none') {
        body.residency = request.residency
      }
      // Throws with the server's message on any failure; the launcher shows
      // it (L3A2-5: a refused start used to reach the console only).
      const data = await postAction<{ success: boolean; error?: string; workflow_id: string }>(
        '/api/projects/start', body, authSession,
      )
      activeLookupRef.current++
      setActiveWorkflowId(data.workflow_id)
      setProjectName(request.projectName)
      setViewedWorkflowName(request.projectName)
      setShowProjectLauncher(false)
      // Refresh project list so the new project appears
      setProjectListKey(k => k + 1)
      // If schedule was set, send it
      if (request.schedule) {
        await fetch('/api/projects/schedule', {
          method: 'POST',
          headers: authSession.getHeaders(),
          body: JSON.stringify({ workflowId: data.workflow_id, ...request.schedule }),
        }).catch(err => console.error('Failed to set schedule:', err))
      }
    } finally {
      setIsLaunching(false)
    }
  }, [authSession])

  // Handle stopping a project workflow. The workflow id is the one the
  // Header's confirmation named, never whatever is selected by the time the
  // user confirms (L3-10).
  const handleStopProject = useCallback(async (workflowId: string) => {
    setActionError(null)
    try {
      await postAction('/api/projects/stop', { workflow_id: workflowId, reason: 'User requested stop' }, authSession)
      setActiveWorkflowId(current => (current === workflowId ? null : current))
    } catch (error) {
      console.error('Error stopping project:', error)
      setActionError(`Stop failed: ${error instanceof Error ? error.message : String(error)}`)
    }
  }, [authSession])

  // Handle a project being archived from Settings — the archived project is the
  // currently-selected one, so clear its live state and refresh the list (it no
  // longer comes back from discovery).
  const handleProjectArchived = useCallback(() => {
    setActiveWorkflowId(null)
    setProjectName('')
    setProjectListKey(k => k + 1)
  }, [])

  // Handle resuming a stopped project workflow
  const handleResumeProject = useCallback(async (intent?: string) => {
    if (!projectName) return
    setActionError(null)
    try {
      const data = await postAction<{ success: boolean; error?: string; workflow_id: string }>(
        '/api/projects/resume', { project_name: projectName, intent: intent || undefined }, authSession,
      )
      activeLookupRef.current++
      setActiveWorkflowId(data.workflow_id)
    } catch (error) {
      console.error('Error resuming project:', error)
      setActionError(`Resume failed: ${error instanceof Error ? error.message : String(error)}`)
    }
  }, [projectName, authSession])

  // Handle shard blocked notification
  const handleShardBlocked = useCallback((shard: Shard) => {
    // Play notification sound
    playNotificationSound(settings.notificationSound)

    // Add visual notification
    const notification: BlockedShardNotification = {
      id: `${shard.id}-${Date.now()}`,
      shardId: shard.id,
      title: shard.title,
      timestamp: Date.now(),
    }
    setBlockedNotifications((prev) => [...prev, notification])
  }, [settings.notificationSound])

  // Dismiss blocked notification
  const handleDismissNotification = useCallback((id: string) => {
    setBlockedNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  // Handle all shards completed
  const handleAllShardsDone = useCallback(() => {
    setShowCompletionModal(true)
    // Play celebratory victory sound if notifications are enabled
    if (settings.notificationSound !== 'none') {
      playNotificationSound('victory')
    }
  }, [settings.notificationSound])

  // Handle marking a shard as complete (for AWAITING_HUMAN_ACTION tasks)
  const handleMarkComplete = useCallback(async (shardId: string) => {
    if (!confirm('Are you sure the infrastructure has been provisioned? This will unblock dependent tasks.')) {
      return;
    }
    try {
      const response = await fetch(`/api/shards/${shardId}/complete`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ project_name: projectName }),
      });
      const data = await response.json();
      if (!data.success) {
        console.error('Failed to mark complete:', data.error);
      }
      // Close the modal — the shard status will update via WebSocket
      setSelectedShard(null);
    } catch (err) {
      console.error('Failed to mark complete:', err);
    }
  }, [projectName, getAuthHeaders]);

  // Handle answering an interaction question
  const handleAnswer = useCallback(async (questionId: string, value: string | string[]) => {
    try {
      const response = await fetch('/api/mcp/answer', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ questionId, value }),
      })
      if (!response.ok) {
        console.error('Failed to submit answer:', response.statusText)
      }
    } catch (error) {
      console.error('Error submitting answer:', error)
    }
  }, [getAuthHeaders])

  const handleLogMessage = (data: any) => {
    // Handle historical:logs event (bulk load)
    if (data.type === 'historical:logs') {
      const entries: LogEntry[] = data.logs.map((log: any) => ({
        id: log.id,
        timestamp: log.timestamp,
        source: typeof log.source === 'string' ? log.source : log.source?.persona || 'activity',
        worker: log.worker,
        persona: log.persona,
        eventType: log.event_type,
        message: log.message,
      }))
      setLogs(entries)
      return
    }

    // Handle log:line events (live updates)
    if (data.type === 'log:line') {
      const newLog: LogEntry = {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date().toISOString(),
        source: 'activity',
        worker: data.worker || undefined,
        persona: data.persona || undefined,
        eventType: data.level || undefined,
        message: data.message || '',
      }
      setLogs(prevLogs => [...prevLogs, newLog])
    }
  }

  // Use a ref to hold message handler so we can pass a stable callback to useWebSocket
  const messageHandlerRef = useRef<(data: any) => void>(() => {})

  // Token guards the pulse-cleanup timeout so rapid clicks don't strip ring classes
  // from cards that a newer click just re-pulsed.
  const awaitingPulseTokenRef = useRef(0)

  // Only change WS URL when auth state transitions (null → token), not on every
  // MSAL token refresh (which issues a new string each time, causing reconnects)
  const [wsAuthToken, setWsAuthToken] = useState<string | null>(null)
  useEffect(() => {
    if (authToken && !wsAuthToken) {
      setWsAuthToken(authToken)
    }
  }, [authToken, wsAuthToken])
  const wsUrl = useMemo(() => getWsUrl(wsAuthToken), [wsAuthToken])
  const { status: wsStatus, reconnect, send } = useWebSocket(
    wsUrl,
    {
      maxReconnectAttempts: Infinity,
      reconnectInterval: 5000,
      onMessage: (data: any) => messageHandlerRef.current(data),
      onConnect: () => {
        console.log('WebSocket connected')
      },
      onDisconnect: () => {
        console.log('WebSocket disconnected')
      },
    }
  )

  // Reconnect WebSocket when app returns to foreground (mobile sleep/background)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && wsStatus === 'disconnected') {
        reconnect()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [wsStatus, reconnect])

  const { shards, handleWebSocketMessage: handleShardMessage, getShardHistory } = useShards({
    onMessage: handleLogMessage,
    onShardBlocked: handleShardBlocked,
    onAllShardsDone: handleAllShardsDone,
  })

  // Count of shards needing human action (drives the header badge)
  const awaitingCount = useMemo(
    () => shards.filter(s => s.status === 'AWAITING_HUMAN_ACTION').length,
    [shards]
  )

  // Switch to board view, scroll the oldest awaiting card into view, and pulse all awaiting cards.
  const handleAwaitingClick = useCallback(() => {
    // Switch to board view if not already there
    setViewMode('board')

    // Defer scroll/pulse until after the view has rendered. Double rAF: the first
    // fires after the next paint (post-commit), the second guarantees layout has
    // settled. This is deterministic relative to React's render cycle, unlike a
    // wall-clock setTimeout that may be too short under load.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const cards = document.querySelectorAll<HTMLElement>('[data-shard-id]')
        const awaitingIds = new Set(
          shards.filter(s => s.status === 'AWAITING_HUMAN_ACTION').map(s => s.id)
        )
        const awaitingCards: HTMLElement[] = []
        cards.forEach(c => {
          const id = c.getAttribute('data-shard-id')
          if (id && awaitingIds.has(id)) awaitingCards.push(c)
        })
        const firstCard = awaitingCards[0]
        if (!firstCard) return

        // Bump the token so any in-flight cleanup from a previous click no-ops.
        const myToken = ++awaitingPulseTokenRef.current

        // Scroll the first one into view
        firstCard.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Pulse all of them with a temporary ring
        const pulseClasses = ['ring-2', 'ring-amber-400', 'ring-offset-2', 'ring-offset-cosmic-abyss']
        awaitingCards.forEach(c => c.classList.add(...pulseClasses))

        // 1500ms is intentional UI duration (how long the pulse lasts), not a
        // render-timing guess. Token-guard ensures only the most recent click's
        // cleanup runs; when it does, it sweeps every element bearing the marker
        // class so any orphans from superseded clicks get cleaned up too.
        setTimeout(() => {
          if (awaitingPulseTokenRef.current !== myToken) return
          document.querySelectorAll<HTMLElement>('[data-shard-id].ring-amber-400').forEach(c => {
            c.classList.remove(...pulseClasses)
          })
        }, 1500)
      })
    })
  }, [shards])

  const {
    workers,
    workerDetails,
    isLoadingDetails,
    requestWorkerDetails,
    clearWorkerDetails,
    handleWebSocketMessage: handleWorkerMessage,
  } = useWorkers({ send })

  // Update the message handler ref
  useEffect(() => {
    messageHandlerRef.current = (data: any) => {
      // Defense-in-depth: ignore events scoped to a different project, except
      // a question and its answer, which are shown whichever project is on
      // screen: an agent waits on its question (L3-3).
      const isInteraction = data.type === 'interaction:pending' || data.type === 'interaction:answered'
      if (!isInteraction && data.project_name && projectName && data.project_name !== projectName) {
        return;
      }

      handleShardMessage(data)
      handleWorkerMessage(data)

      // Handle interaction events
      if (data.type === 'interaction:pending') {
        // Transform backend format to frontend Question type
        // Backend sends: { type, question_id, worker_name, question_type: { type, question, options?, dialogue? }, dialogue? }
        // Frontend expects: { id, workerId, workerName, type, prompt, options?, dialogue? }
        // Note: dialogue can come from either the top-level field or inside question_type
        const dialogue = data.dialogue || data.question_type?.dialogue
        const question: Question = {
          id: data.question_id,
          workerId: data.worker_name,
          workerName: data.worker_name,
          type: data.question_type?.type || 'text',
          prompt: data.question_type?.question || '',
          options: data.question_type?.options?.map((opt: string, idx: number) => ({
            id: `opt-${idx}`,
            label: opt,
            value: opt,
          })),
          timestamp: data.timestamp || new Date().toISOString(),
          dialogue,
          projectName: data.project_name,
        }
        setQuestions((prev) => {
          if (prev.some(q => q.id === data.question_id)) {
            console.warn(`Duplicate question ignored: ${data.question_id}`)
            return prev
          }
          return [...prev, question]
        })
      } else if (data.type === 'interaction:answered') {
        setQuestions((prev) => prev.filter((q) => q.id !== data.question_id))
      }

      // Handle project switch - clear all state and discover active workflow
      else if (data.type === 'project:switched') {
        console.log('Project switched to:', data.name)
        setProjectName(data.name)
        // Clear all state - fresh data will arrive via WebSocket, the pending
        // questions of every project included
        setLogs([])
        setQuestions([])
        setBlockedNotifications([])
        setSelectedShard(null)
        setSelectedWorker(null)
        // Discover active workflow for this project (enables Stop button).
        // Must query by the true project_name (data.workflow_name), not the
        // repo name (data.name) — they diverge whenever setupProject hit a
        // repo-name collision (found live 2026-07-06: every collided
        // project showed "Resume" instead of "Stop" despite being active).
        discoverActiveWorkflow(data.workflow_name || data.name)
      }
    }
  }, [handleShardMessage, handleWorkerMessage, discoverActiveWorkflow, projectName])


  // Poll Temporal workflow status to supplement shard data with real-time running task info.
  const [temporalRunningTasks, setTemporalRunningTasks] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!activeWorkflowId) {
      setTemporalRunningTasks(new Set())
      return
    }
    let cancelled = false
    const poll = async () => {
      try {
        const resp = await fetch(`/api/projects/status/${encodeURIComponent(activeWorkflowId)}`, { headers: getAuthHeaders() })
        if (!resp.ok) return
        const data = await resp.json()
        if (!cancelled && Array.isArray(data.running)) {
          setTemporalRunningTasks(new Set(data.running as string[]))
        }
      } catch { /* ignore */ }
    }
    poll()
    const interval = setInterval(poll, 15000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [activeWorkflowId, getAuthHeaders])

  // Supplement shards: if Temporal says a task is running but git still shows TODO, override to IN_PROGRESS
  const augmentedShards = useMemo(() => {
    if (temporalRunningTasks.size === 0) return shards
    return shards.map(s => {
      if (temporalRunningTasks.has(s.id) && (s.status === 'TODO' || s.status === 'READY')) {
        return { ...s, status: 'IN_PROGRESS' as const }
      }
      return s
    })
  }, [shards, temporalRunningTasks])

  // Map workers to GraphView format — derive liveness from Temporal running tasks
  const graphWorkers: GraphWorker[] = useMemo(() => workers.map((w) => {
    const isRunning = temporalRunningTasks.size > 0 && augmentedShards.some(s =>
      (s.assigned_worker === w.name || s.assignee === w.persona) &&
      temporalRunningTasks.has(s.id)
    )
    return {
      id: w.name,
      name: w.name,
      persona: w.persona || 'voyager',
      state: (isRunning || w.status === 'active') ? 'active' as const : 'inactive' as const,
    }
  }), [workers, temporalRunningTasks, augmentedShards])

  // Map shards to GraphView format (memoized to prevent infinite re-renders)
  const graphShards: GraphShard[] = useMemo(() => augmentedShards.map((s) => ({
    id: s.id,
    type: s.id.startsWith('SPEC') ? 'SPEC' : 'TASK',
    status: s.status,
    assignee: s.assigned_worker || null,
    dependencies: [],
  })), [augmentedShards])

  // Request notification permission and subscribe to push once auth is ready
  useEffect(() => {
    // Wait for auth to be ready before subscribing (token needed for API call)
    if (msalEnabled && !authToken) return

    async function setupPushNotifications() {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) return

      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return

      try {
        const registration = await navigator.serviceWorker.ready
        let subscription = await registration.pushManager.getSubscription()

        if (!subscription) {
          const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY
          if (!vapidPublicKey) {
            console.warn('VITE_VAPID_PUBLIC_KEY not set, push notifications disabled')
            return
          }

          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidPublicKey,
          })
        }

        // Send subscription to server
        const pushHeaders: Record<string, string> = { 'Content-Type': 'application/json' }
        if (authToken) pushHeaders['Authorization'] = `Bearer ${authToken}`
        await fetch('/api/push/subscribe', {
          method: 'POST',
          headers: pushHeaders,
          body: JSON.stringify(subscription.toJSON()),
        })
      } catch (err) {
        console.warn('Push notification setup failed:', err)
      }
    }

    setupPushNotifications()
  }, [authToken])

  // Hide loader after initial connection or timeout
  useEffect(() => {
    if (wsStatus === 'connected') {
      setIsInitializing(false)
    }

    // Timeout after 5 seconds even if not connected
    const timeout = setTimeout(() => {
      setIsInitializing(false)
    }, 5000)

    return () => clearTimeout(timeout)
  }, [wsStatus])

  if (authLoading) {
    return <Loader message="Authenticating with Entra ID..." />
  }

  if (isInitializing) {
    return <Loader message="Connecting to Star Forge..." />
  }

  return (
    <div className="h-full bg-cosmic-void text-white flex flex-col min-h-0">
      {wsStatus === 'disconnected' && <DisconnectWarning onReconnect={reconnect} />}

      {/* Blocked shard notifications */}
      <BlockedShardToast
        notifications={blockedNotifications}
        onDismiss={handleDismissNotification}
      />

      {/* Interaction panel for human-in-the-loop questions */}
      <InteractionPanel
        questions={questions}
        viewedProject={viewedWorkflowName || projectName}
        onAnswer={handleAnswer}
        notificationSound={settings.notificationSound as 'none' | 'chime' | 'bell' | 'subtle'}
      />

      {/* Project Launcher modal */}
      {showProjectLauncher && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowProjectLauncher(false) }}
        >
          <ProjectLauncher
            onLaunch={handleLaunchProject}
            isLaunching={isLaunching}
            graphToken={graphToken}
            graphTokenError={graphTokenError}
          />
        </div>
      )}

      <Header
        projectName="Observation Deck"
        wsStatus={wsStatus}
        awaitingCount={awaitingCount}
        onAwaitingClick={handleAwaitingClick}
        onSettingsClick={() => setIsSettingsOpen(true)}
        currentProjectName={projectName}
        onProjectChange={(name, workflowName) => {
          setProjectName(name)
          // Discover active workflow for the selected project. Must query by
          // the true project_name (workflowName), not the repo name — they
          // diverge whenever setupProject hit a repo-name collision (found
          // live 2026-07-06: every collided project showed "Resume" instead
          // of "Stop" despite being active).
          discoverActiveWorkflow(workflowName || name)
        }}
        onNewProject={() => setShowProjectLauncher(true)}
        stopWorkflowId={activeWorkflowId}
        onStopProject={activeWorkflowId ? handleStopProject : undefined}
        onResumeProject={!activeWorkflowId && projectName ? handleResumeProject : undefined}
        projectListKey={projectListKey}
        getAuthHeaders={getAuthHeaders}
      />

      {actionError && (
        <div role="alert" className="mx-4 mt-2 flex items-start justify-between gap-3 px-3 py-2 text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-lg">
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} className="text-red-300 hover:text-white" aria-label="Dismiss">
            Dismiss
          </button>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden min-h-0">
      <main className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* View Toggle */}
        <div className="flex items-center justify-between px-4 py-2 bg-cosmic-abyss border-b border-cosmic-border overflow-x-auto">
          <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('graph')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              viewMode === 'graph'
                ? 'bg-cosmic-nebula text-white'
                : 'bg-cosmic-surface text-gray-400 hover:text-white'
            }`}
          >
            Graph View
          </button>
          <button
            onClick={() => setViewMode('board')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              viewMode === 'board'
                ? 'bg-cosmic-nebula text-white'
                : 'bg-cosmic-surface text-gray-400 hover:text-white'
            }`}
          >
            Board
          </button>
          <button
            onClick={() => setViewMode('mission-control')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              viewMode === 'mission-control'
                ? 'bg-cosmic-nebula text-white'
                : 'bg-cosmic-surface text-gray-400 hover:text-white'
            }`}
          >
            Mission Control
          </button>
          </div>
          <button
            onClick={() => setIsLogsOpen(prev => !prev)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              isLogsOpen
                ? 'bg-cosmic-nebula text-white'
                : 'bg-cosmic-surface text-gray-400 hover:text-white'
            }`}
          >
            Logs ({logs.length})
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden min-h-0">
          {viewMode === 'graph' ? (
            <GraphView
              workers={graphWorkers}
              shards={graphShards}
              onWorkerClick={(id) => {
                const worker = workers.find((w) => w.name === id)
                if (worker) {
                  setSelectedWorker(worker)
                  requestWorkerDetails(worker.name)
                }
              }}
              onShardClick={(id) => {
                const shard = shards.find((s) => s.id === id)
                if (shard) setSelectedShard(shard)
              }}
            />
          ) : viewMode === 'board' ? (
            <BoardView
              shards={augmentedShards}
              workers={workers}
              onShardClick={(shard) => setSelectedShard(shard)}
              onWorkerClick={(worker) => {
                setSelectedWorker(worker)
                requestWorkerDetails(worker.name)
              }}
            />
          ) : (
            <MissionControl
              shards={augmentedShards}
              workers={workers}
              logs={logs}
              onShardClick={(shard) => setSelectedShard(shard)}
              onWorkerClick={(worker) => {
                setSelectedWorker(worker)
                requestWorkerDetails(worker.name)
              }}
              projectName={projectName}
              residency={projectResidency}
              getAuthHeaders={getAuthHeaders}
            />
          )}
        </div>
      </main>

      <LogsPanel logs={logs} isOpen={isLogsOpen} onToggle={() => setIsLogsOpen(false)} getAuthHeaders={getAuthHeaders} />
      </div>

      <Settings isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} projectName={projectName} getAuthHeaders={getAuthHeaders} graphToken={graphToken} graphTokenError={graphTokenError} onSignOut={msalEnabled ? handleSignOut : undefined} onProjectArchived={handleProjectArchived} />

      {selectedShard && (
        <ShardInspectorModal
          shard={{
            id: selectedShard.id,
            title: selectedShard.title,
            status: selectedShard.status,
            owner: selectedShard.assigned_worker || selectedShard.assignee || 'Unassigned',
            yaml: selectedShard.spec || `# ${selectedShard.id}\ntitle: ${selectedShard.title}\nstatus: ${selectedShard.status}`,
            history: getShardHistory(selectedShard.id),
            depends_on: selectedShard.depends_on,
            scratchpad: selectedShard.scratchpad,
          }}
          isOpen={true}
          onClose={() => setSelectedShard(null)}
          onMarkComplete={handleMarkComplete}
          onShardClick={(shardId) => {
            const target = augmentedShards.find(s => s.id === shardId)
            if (target) setSelectedShard(target)
          }}
          getShardStatus={(shardId) => augmentedShards.find(s => s.id === shardId)?.status}
          getAuthHeaders={getAuthHeaders}
          projectName={projectName}
        />
      )}

      {selectedWorker && (
        <WorkerModal
          worker={{
            name: selectedWorker.name,
            role: selectedWorker.persona || 'voyager',
            status: selectedWorker.status,
            currentTask: undefined,
            persona: {
              graphic: '',
              description: `${selectedWorker.persona || 'voyager'} worker`,
            },
            heartbeats: workerDetails?.heartbeats || [],
            activityLog: workerDetails?.activity_log || [],
          }}
          isOpen={true}
          isLoading={isLoadingDetails}
          onClose={() => {
            setSelectedWorker(null)
            clearWorkerDetails()
          }}
        />
      )}

      {/* Completion modal - shown when all shards are done */}
      <CompletionModal
        isOpen={showCompletionModal}
        totalShards={shards.length}
        projectName={projectName}
        getAuthHeaders={getAuthHeaders}
        onDismiss={() => setShowCompletionModal(false)}
      />
    </div>
  )
}

export default App
